'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface PaymentTerm {
  title: string;
  content: string;
  details: string[];
}

export default function PaymentTermsSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const paymentTerms: PaymentTerm[] = [
    {
      title: 'Accepted Payment Methods',
      content: 'We accept multiple payment options for your convenience, including cards, bank transfers, and digital wallets.',
      details: [
        'Credit Cards: Visa, Mastercard, American Express, Diners Club',
        'Debit Cards: All major Indian banks',
        'UPI: Google Pay, PhonePe, Paytm, BHIM',
        'Net Banking: All major Indian banks',
        'Bank Transfer: NEFT/RTGS for advance bookings',
        'Corporate Invoicing: Available for registered business accounts',
      ],
    },
    {
      title: 'Payment Schedule',
      content: 'Payment timing varies based on booking duration and advance notice.',
      details: [
        'Short Stays (1-6 nights): 100% payment at time of booking',
        'Weekly Stays (7-29 nights): 50% advance, balance 48 hours before check-in',
        'Monthly Bookings (30+ nights): 30% advance, balance on check-in',
        'Corporate Accounts: Net 30 payment terms available',
        'Last-minute bookings (within 48 hours): Full payment required',
      ],
    },
    {
      title: 'Security Deposit',
      content: 'Refundable security deposit required at check-in to cover incidental charges and potential damages.',
      details: [
        'Luxos Luxury: ₹5,000 security deposit',
        'Primera Premium: ₹4,000 security deposit',
        'Classica Comfort: ₹3,000 security deposit',
        'Deposit held as credit card authorization (not charged)',
        'Released within 48 hours of check-out after inspection',
        'Deductions made only for damages or missing items',
        'Corporate accounts may have deposit waived',
      ],
    },
    {
      title: 'Currency & Pricing',
      content: 'All rates quoted in Indian Rupees (₹) with USD pricing available for international guests.',
      details: [
        'Primary currency: Indian Rupees (₹)',
        'USD pricing available at prevailing exchange rates',
        'International cards accepted with standard forex charges',
        'GST (5%) included in all quoted rates',
        'Rates subject to change during peak seasons',
        'Special rates for extended stays and corporate bookings',
      ],
    },
    {
      title: 'Additional Charges',
      content: 'Extra services and amenities available at additional cost beyond base room rate.',
      details: [
        'Extra Guest: ₹1,000 per night (subject to occupancy limits)',
        'Airport Transfers: ₹1,500 one-way (sedan), ₹2,500 (SUV)',
        'Laundry Services: As per service menu pricing',
        'Late Check-out: 50% of daily rate (subject to availability)',
        'Pet Accommodation: ₹1,500 per night (pre-approval required)',
        'Parking: Complimentary for registered guests',
      ],
    },
    {
      title: 'Corporate Billing',
      content: 'Streamlined invoicing and payment processes for business accounts.',
      details: [
        'GST invoices provided for all bookings',
        'Net 30 payment terms for approved corporate accounts',
        'Consolidated monthly billing available',
        'Direct billing to company with authorization letter',
        'Detailed expense reports for corporate compliance',
        'Dedicated account manager for corporate clients',
      ],
    },
  ];

  const toggleItem = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="payment-terms" className="py-16 lg:py-24 bg-background">
      <div className="max-w-5xl mx-auto px-4 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 text-accent mb-4">
            <Icon name="CreditCardIcon" size={24} />
            <span className="text-sm font-semibold uppercase tracking-wide">Payment Terms</span>
          </div>
          <h2 className="font-display text-3xl lg:text-5xl font-bold text-primary mb-4">
            Payment Conditions
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            Flexible payment options including accepted methods, security deposits, currency handling, and corporate invoicing procedures.
          </p>
        </div>

        <div className="space-y-4">
          {paymentTerms.map((term, index) => (
            <div
              key={index}
              className="bg-white rounded-xl shadow-subtle border border-border overflow-hidden hover:shadow-elevated transition-all duration-300"
            >
              <button
                onClick={() => toggleItem(index)}
                className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-muted transition-colors duration-300"
              >
                <div className="flex-1 pr-4">
                  <h3 className="font-display text-xl font-semibold text-primary mb-2">
                    {term.title}
                  </h3>
                  <p className="text-text-secondary text-sm">{term.content}</p>
                </div>
                <Icon
                  name={openIndex === index ? 'ChevronUpIcon' : 'ChevronDownIcon'}
                  size={24}
                  className="text-text-secondary flex-shrink-0"
                />
              </button>

              {openIndex === index && (
                <div className="px-6 pb-6 animate-slide-in-left">
                  <div className="border-t border-border pt-4">
                    <ul className="space-y-3">
                      {term.details.map((detail, idx) => (
                        <li key={idx} className="flex items-start space-x-3">
                          <Icon
                            name="CheckCircleIcon"
                            size={20}
                            className="text-accent flex-shrink-0 mt-0.5"
                          />
                          <span className="text-text-secondary text-sm leading-relaxed">
                            {detail}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-8 bg-accent/10 border border-accent/30 rounded-xl p-6">
          <div className="flex items-start space-x-3">
            <Icon name="ShieldCheckIcon" size={24} className="text-accent flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-semibold text-primary mb-2">Secure Payment Processing</h4>
              <p className="text-sm text-text-secondary leading-relaxed">
                All online transactions are processed through PCI-DSS compliant payment gateways with 256-bit SSL encryption. We never store your complete card details on our servers. Your payment information is safe and secure.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}